// TOEFL iBT Mock 6 — Writing (2026 format, advanced difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets an
//                       advanced structure (past perfect sequencing, "so…that"
//                       inversion, "were it not for", neither…nor, passive
//                       infinitives). `accept` lists other word orders that
//                       are also grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal complaint that must give evidence,
//                       describe impact and justify a specific resolution.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       AND explain a practical obstacle and how to overcome it.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Why didn't Jonah present at the symposium?",
    prefix: "He was told that",
    answer: ["his", "abstract", "had", "been", "submitted", "after", "the", "deadline", "had", "passed"],
    suffix: ".",
    distractors: ["has", "passing"],
  },
  {
    id: 2,
    speaker: "Can any student use the new electron microscope?",
    prefix: "Only those",
    answer: ["who", "have", "completed", "the", "safety", "training", "required", "by", "the", "department"],
    suffix: ".",
    distractors: ["whom", "requiring"],
  },
  {
    id: 3,
    speaker: "What did you think of the documentary about coral reefs?",
    prefix: "So",
    answer: ["disturbing", "were", "the", "images", "that", "I", "could", "barely", "keep", "watching"],
    suffix: ".",
    distractors: ["was", "disturbed"],
  },
  {
    id: 4,
    speaker: "Do you know why the seminar was moved to Friday?",
    prefix: "I have no idea",
    answer: ["why", "the", "organizers", "decided", "to", "change", "the", "schedule", "so", "late"],
    accept: [["why", "the", "organizers", "decided", "so", "late", "to", "change", "the", "schedule"]],
    suffix: ".",
    distractors: ["did", "changing"],
  },
  {
    id: 5,
    speaker: "Are you going to apply for the summer research grant?",
    prefix: "I would,",
    answer: ["were", "it", "not", "for", "the", "fact", "that", "I", "have", "an", "internship"],
    suffix: ".",
    distractors: ["was", "having"],
  },
  {
    id: 6,
    speaker: "Did the city council approve the bike-lane proposal?",
    prefix: "Yes, and",
    answer: ["construction", "is", "expected", "to", "begin", "as", "soon", "as", "funding", "is", "secured"],
    suffix: ".",
    distractors: ["will", "securing"],
  },
  {
    id: 7,
    speaker: "Why are you rewriting the entire introduction to your paper?",
    prefix: "My advisor",
    answer: ["pointed", "out", "that", "it", "lacked", "a", "clear", "statement", "of", "purpose"],
    suffix: ".",
    distractors: ["lacking", "which"],
  },
  {
    id: 8,
    speaker: "Have you heard anything about the new library hours?",
    prefix: "Not officially,",
    answer: ["but", "the", "library", "is", "rumored", "to", "be", "staying", "open", "until", "midnight"],
    suffix: ".",
    distractors: ["rumors", "stay"],
  },
  {
    id: 9,
    speaker: "Why did the professor give your group an extension?",
    prefix: "She understood that",
    answer: ["neither", "my", "partner", "nor", "I", "had", "been", "given", "access", "to", "the", "data"],
    accept: [["neither", "I", "nor", "my", "partner", "had", "been", "given", "access", "to", "the", "data"]],
    suffix: ".",
    distractors: ["or", "have"],
  },
  {
    id: 10,
    speaker: "Why is the department hiring so many new lecturers?",
    prefix: "Enrollment has grown so quickly that",
    answer: ["existing", "staff", "can", "no", "longer", "handle", "the", "number", "of", "courses"],
    suffix: ".",
    distractors: ["any", "handling"],
  },
];

export const emailTask = {
  recipient: "Mr. Okonkwo",
  scenario:
    "You organized a two-day conference for graduate students at your university. Two weeks before the event, you sent the catering company a detailed order that listed the dietary requirements of all 85 attendees. On the first day, the lunch delivered did not match these requirements, and around twenty attendees were unable to eat. The company has now sent an invoice for the full amount. Write an email to Mr. Okonkwo, the catering manager.",
  points: [
    "Explain what went wrong with the order and show that the company had received your requirements.",
    "Describe how the problem affected the conference and its attendees.",
    "State what resolution you expect regarding the invoice, and explain why it is fair.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["order", "ordered", "requirement", "requirements", "dietary", "vegetarian", "vegan", "gluten", "allergy", "allergies", "halal", "kosher", "list", "sent", "submitted", "confirmed", "confirmation", "two weeks", "in advance", "attached", "written", "record", "did not match", "didn't match", "instead", "wrong", "mistake", "error"],
    ["attendees", "participants", "guests", "twenty", "20", "hungry", "unable to eat", "could not eat", "couldn't eat", "embarrassed", "embarrassing", "complain", "complained", "reputation", "speakers", "schedule", "delay", "delayed", "had to buy", "left", "upset", "impression", "affected", "afternoon"],
    ["invoice", "refund", "discount", "reduce", "reduction", "reduced", "partial", "portion", "percent", "%", "deduct", "credit", "compensation", "compensate", "pay only", "full amount", "fair", "reasonable", "since", "because", "therefore", "expect", "request", "would like"],
  ],
  formal: true,
  minWords: 110,
  lengthSteps: [[150, 5], [125, 4.5], [105, 4], [85, 3], [65, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "technology and society",
  professor: "Dr. Lindqvist",
  question:
    "Many large companies now use AI systems to screen job applications, and a large share of applicants are rejected before any person reads their materials. Supporters say these systems are faster and more consistent than human recruiters; critics say they can reproduce hidden biases and give applicants no way to respond. Should companies be legally required to tell applicants when an algorithm has rejected them and to offer a review by a human? In your post, also describe the biggest practical difficulty with your position and how it could be overcome.",
  posts: [
    {
      name: "Aisha",
      text: "Yes, this should be required. People have a right to know how decisions about their lives are made. If a system screens out applicants with gaps in their work history, for instance, parents returning to work could be rejected unfairly without ever finding out why. A human review is the only real safeguard.",
    },
    {
      name: "Lukas",
      text: "I don't think a legal requirement is realistic. Large companies receive thousands of applications per position, so offering everyone a human review would be enormously expensive. Human recruiters are biased too. It would be better to require regular independent audits of the algorithms themselves.",
    },
  ],
  topicKeywords: [
    "ai", "algorithm", "algorithms", "automated", "system", "systems", "software", "company", "companies",
    "employer", "employers", "applicant", "applicants", "application", "applications", "job", "jobs", "hiring",
    "recruiter", "recruiters", "reject", "rejected", "rejection", "bias", "biased", "fair", "unfair", "fairness",
    "transparency", "transparent", "disclose", "disclosure", "human", "review", "law", "legal", "regulation", "audit", "audits",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on requiring disclosure and human review",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "should be required", "should not be required", "shouldn't be required", "should be legally", "legal requirement", "law should", "require", "required", "disclose", "disclosure", "right to know", "human review", "inform applicants", "tell applicants", "mandatory"],
    },
    {
      label: "Describe the biggest practical difficulty and how it could be overcome",
      keywords: ["difficulty", "challenge", "obstacle", "drawback", "practical", "expensive", "volume", "overcome", "solve", "solution", "address this", "deal with", "could be handled", "one way", "only applicants who", "request it", "appeal", "threshold", "random sample", "small fee", "phase in", "gradually"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
